import { Notification as customNotification } from '../../components/ui/custom-notification';
import get from 'lodash/get';
import axiosConfig from './axiosConfig';
import axios from 'axios';

/**
 * Get all registries grouped by equipment_type
 * @param {Object} payload - Optional payload with project_id
 * @returns {Promise<Object>} - Object with equipment types as keys and arrays as values
 */
export const getAllRegistries = async (payload = {}) => {
  try {
    let url = '/registry/getAllRegistries';
    
    // Add project_id query param if provided
    if (payload.project_id) {
      url += `?project_id=${payload.project_id}`;
    }

    const res = await axiosConfig.get(url);

    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return {};
    }
    
    if (res.status === 200) {
      const data = get(res, ['data', 'data'], {});
      return data || {};
    }
    
    return {};
  } catch (error) {
    customNotification.error('Failed to fetch registries', error.data || error.message);
    return {};
  }
};

/**
 * Create or update registries in bulk
 * @param {Object} payload - Object with items array: { items: [...] }
 * @returns {Promise<Object>} - Response with success/failure status for each item
 */
export const bulkCreateUpdateRegistry = async (payload) => {
  try {
    const url = '/registry/bulkCreateUpdateRegistry';

    const res = await axiosConfig.post(url, payload);

    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return null;
    }
    
    if (res.status === 200) {
      const responseData = get(res, ['data', 'data'], {});
      customNotification.success(
        'Registry saved successfully',
        'The equipment registry has been saved successfully.'
      );
      return responseData;
    }
    
    return null;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to save registry';
    customNotification.error('Failed to save registry', errorMessage);
    return null;
  }
};

/**
 * Delete registries in bulk
 * @param {Object} payload - Object with ids array: { ids: [1, 2, 3, ...] }
 * @returns {Promise<Object>} - Response with deletion statistics
 */
export const bulkDeleteRegistry = async (payload) => {
  try {
    const url = '/registry/bulkDeleteRegistry';

    const res = await axiosConfig.post(url, payload);

    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return null;
    }
    
    if (res.status === 200) {
      const responseData = get(res, ['data', 'data'], {});
      customNotification.success(
        'Registries deleted successfully',
        'The selected registries have been deleted successfully.'
      );
      return responseData;
    }
    
    return null;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to delete registries';
    customNotification.error('Failed to delete registries', errorMessage);
    return null;
  }
};

/**
 * Delete registry documents by document IDs
 * @param {Object} payload - Object with docIds array: { docIds: [1, 2, 3, ...] }
 * @returns {Promise<Object>} - Response with deletion statistics
 */
export const deleteRegistryDocumentsByDocId = async (payload) => {
  try {
    const url = '/registry/deleteRegistryDocumentsByDocId';

    const res = await axiosConfig.post(url, payload);

    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return null;
    }
    
    if (res.status === 200) {
      const responseData = get(res, ['data', 'data'], {});
      customNotification.success(
        'Documents deleted successfully',
        'The selected documents have been deleted successfully.'
      );
      return responseData;
    }
    
    return null;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to delete documents';
    customNotification.error('Failed to delete documents', errorMessage);
    return null;
  }
};



// Function to upload files to S3
export const getPreSignedUrlToUpload = async (fileList, payload, version = 1) => {
  try {
    const uploadedFiles = [];

    if (fileList && fileList.length > 0) {
      // Create file objects for all files
      const fileObjs = fileList.map(file => ({
        fileName: file.name.replace(/\(\d+\)/g, "").trim(), // Remove (1), (2), etc.
        contentType: file.type,
        projectId: payload.projectId,
        projectTypeId: payload.projectTypeId,
        type: payload.type,
        fileSize: file.size, // Use actual file size
        version: version || 1
      }));


      // Get signed URLs for all files at once
      const response = await axiosConfig.post(`/registry/getPresignedUrlToUploadRegistryDoc`, fileObjs);

      if (response.status === 200) {
        const presignedData = response.data.data;

        // Process each presigned URL
        for (const data of presignedData) {
          const file = fileList.find(f => f.name.replace(/\(\d+\)/g, "").trim() === data.fileName);

          if (file) {

            // Validate presigned URL data
            if (!data.presignedUrl) {
              customNotification.error('Upload Failed', 'Failed to get upload URL from server.');
              continue; // Skip this file and continue with next
            }

            try {
              // Log the presigned URL (remove sensitive parts for security)
              const urlParts = data.presignedUrl.split('?');


              // Small delay to ensure URL is ready (optional)
              await new Promise(resolve => setTimeout(resolve, 100));

              // Upload file to S3
              const uploadResponse = await axios.put(data.presignedUrl, file, {
                headers: {
                  "Content-Type": file.type || "application/octet-stream",
                  // "Content-Disposition": `attachment; filename="${file.name}"`
                },
                // Add timeout to prevent hanging requests
               // timeout: 100000,
              });

              if (uploadResponse.status === 200) {
                uploadedFiles.push({ s3Key: data.s3Key, fileName: data.fileName });
              } else {
                customNotification.error('File Upload Failed', `Failed to upload ${file.name}. Please try again.`);
              }
            } catch (error) {
              // Add more detailed error logging
              if (error.response) {
              }
              // Show user-friendly error
              customNotification.error('File Upload Failed', `Failed to upload ${file.name}. Please try again.`);
            }
          } else {
            customNotification.error('File Upload Failed', `File ${data.fileName} not found.`);
          }
        }
      } else {
        customNotification.error('Upload Failed', 'Failed to get upload URLs from server.');
      }

    }
    return uploadedFiles;
  } catch (error) {
    return []; // Return empty array on error
  }
};

/**
 * Get all consumables grouped by consumable_type
 * @param {Object} payload - Optional payload with project_id
 * @returns {Promise<Object>} - Object with consumable types as keys and arrays as values
 */
export const getAllConsumables = async (payload = {}) => {
  try {
    let url = '/registryConsumable/getAllRegistryConsumables';
    
    // Add project_id query param if provided
    if (payload.project_id) {
      url += `?project_id=${payload.project_id}`;
    }

    const res = await axiosConfig.get(url);

    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return {};
    }
    
    if (res.status === 200) {
      const data = get(res, ['data', 'data'], {});
      return data || {};
    }
    
    return {};
  } catch (error) {
    // Check if it's a database schema error
    const errorMessage = get(error, 'response.data.data', error.data || error.message || 'Unknown error');
    const errorStatusCode = get(error, 'response.status', error.statusCode);
    
    if (errorStatusCode === 410 || (typeof errorMessage === 'string' && errorMessage.includes('Unknown column'))) {
      console.error('Backend database schema error:', errorMessage);
      customNotification.error(
        'Database Error', 
        'There is a database schema mismatch. Please contact the backend team to fix the SQL query. Error: ' + errorMessage
      );
    } else {
      customNotification.error('Failed to fetch consumables', errorMessage);
    }
    return {};
  }
};

/**
 * Create or update consumables in bulk
 * @param {Object} payload - Object with items array: { items: [...] }
 * @returns {Promise<Object>} - Response with success/failure status for each item
 */
export const bulkCreateUpdateConsumable = async (payload) => {
  try {
    const url = '/registryConsumable/bulkCreateUpdateRegistryConsumable';

    const res = await axiosConfig.post(url, payload);

    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return null;
    }
    
    if (res.status === 200) {
      const responseData = get(res, ['data', 'data'], {});
      customNotification.success(
        'Consumable saved successfully',
        'The consumable registry has been saved successfully.'
      );
      return responseData;
    }
    
    return null;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to save consumable';
    customNotification.error('Failed to save consumable', errorMessage);
    return null;
  }
};

/**
 * Delete consumables in bulk
 * @param {Object} payload - Object with ids array: { ids: [1, 2, 3, ...] }
 * @returns {Promise<Object>} - Response with deletion statistics
 */
export const bulkDeleteConsumable = async (payload) => {
  try {
    const url = '/registryConsumable/bulkDeleteRegistryConsumable';

    const res = await axiosConfig.post(url, payload);

    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return null;
    }
    
    if (res.status === 200) {
      const responseData = get(res, ['data', 'data'], {});
      customNotification.success(
        'Consumables deleted successfully',
        'The selected consumables have been deleted successfully.'
      );
      return responseData;
    }
    
    return null;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to delete consumables';
    customNotification.error('Failed to delete consumables', errorMessage);
    return null;
  }
};

/**
 * Delete consumable documents by document IDs
 * @param {Object} payload - Object with docIds array: { docIds: [1, 2, 3, ...] }
 * @returns {Promise<Object>} - Response with deletion statistics
 */
export const deleteConsumableDocumentsByDocId = async (payload) => {
  try {
    const url = '/registryConsumable/deleteRegistryConsumableDocumentsByDocId';

    const res = await axiosConfig.post(url, payload);

    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return null;
    }
    
    if (res.status === 200) {
      const responseData = get(res, ['data', 'data'], {});
      customNotification.success(
        'Documents deleted successfully',
        'The selected documents have been deleted successfully.'
      );
      return responseData;
    }
    
    return null;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to delete documents';
    customNotification.error('Failed to delete documents', errorMessage);
    return null;
  }
};

/**
 * Get all QC Inspection Personnel
 * @param {Object} payload - Optional payload with project_id
 * @returns {Promise<Object|Array>} - Object grouped by qualification type or Array of QC Inspection Personnel items
 */
export const getAllInspectionPersonnel = async (payload = {}) => {
  try {
    let url = '/inspectionPersonnelRegistry/getAllInspectionPersonnel';
    
    // Add project_id query param if provided
    if (payload.project_id) {
      url += `?project_id=${payload.project_id}`;
    }

    const res = await axiosConfig.get(url);

    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return {};
    }
    
    if (res.status === 200) {
      const data = get(res, ['data', 'data'], {});
      // Return data as-is (could be object grouped by qualification type or array)
      return data || {};
    }
    
    return {};
  } catch (error) {
    const errorMessage = get(error, 'response.data.data', error.data || error.message || 'Unknown error');
    const errorStatusCode = get(error, 'response.status', error.statusCode);
    
    if (errorStatusCode === 410 || (typeof errorMessage === 'string' && errorMessage.includes('Unknown column'))) {
      console.error('Backend database schema error:', errorMessage);
      customNotification.error(
        'Database Error', 
        'There is a database schema mismatch. Please contact the backend team to fix the SQL query. Error: ' + errorMessage
      );
    } else {
      customNotification.error('Failed to fetch QC Inspection Personnel', errorMessage);
    }
    return {};
  }
};

/**
 * Create or update QC Inspection Personnel in bulk
 * @param {Object} payload - Object with items array: { items: [...] }
 * @returns {Promise<Object>} - Response with success/failure status for each item
 */
export const bulkCreateUpdateInspectionPersonnel = async (payload) => {
  try {
    const url = '/inspectionPersonnelRegistry/bulkCreateUpdateInspectionPersonnel';

    const res = await axiosConfig.post(url, payload);

    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return null;
    }
    
    if (res.status === 200) {
      const responseData = get(res, ['data', 'data'], {});
      customNotification.success(
        'QC Inspection Personnel saved successfully',
        'The QC Inspection Personnel has been saved successfully.'
      );
      return responseData;
    }
    
    return null;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to save QC Inspection Personnel';
    customNotification.error('Failed to save QC Inspection Personnel', errorMessage);
    return null;
  }
};

/**
 * Delete QC Inspection Personnel in bulk
 * @param {Object} payload - Object with ids array: { ids: [1, 2, 3, ...] }
 * @returns {Promise<Object>} - Response with deletion statistics
 */
export const bulkDeleteInspectionPersonnel = async (payload) => {
  try {
    const url = '/inspectionPersonnelRegistry/bulkDeleteInspectionPersonnel';

    const res = await axiosConfig.post(url, payload);

    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return null;
    }
    
    if (res.status === 200) {
      const responseData = get(res, ['data', 'data'], {});
      customNotification.success(
        'QC Inspection Personnel deleted successfully',
        'The selected QC Inspection Personnel have been deleted successfully.'
      );
      return responseData;
    }
    
    return null;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to delete QC Inspection Personnel';
    customNotification.error('Failed to delete QC Inspection Personnel', errorMessage);
    return null;
  }
};

/**
 * Delete QC Inspection Personnel documents by document IDs
 * @param {Object} payload - Object with docIds array: { docIds: [1, 2, 3, ...] }
 * @returns {Promise<Object>} - Response with deletion statistics
 */
export const deleteInspectionPersonnelDocumentsByDocId = async (payload) => {
  try {
    const url = '/inspectionPersonnelRegistry/deleteInspectionPersonnelDocumentsByDocId';

    const res = await axiosConfig.post(url, payload);

    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return null;
    }
    
    if (res.status === 200) {
      const responseData = get(res, ['data', 'data'], {});
      customNotification.success(
        'Documents deleted successfully',
        'The selected documents have been deleted successfully.'
      );
      return responseData;
    }
    
    return null;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to delete documents';
    customNotification.error('Failed to delete documents', errorMessage);
    return null;
  }
};

export const getAllConsumablesSaga = async ({ project_id }) => {
  try {
    const url = `/materialTraceability/getMaterialTraceabilityDataByProjectId?project_id=${project_id}`
    const res = await axiosConfig.get(url);
    
    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return null;
    }
    
    if (res.status === 200) {
      const responseData = get(res, ['data', 'data'], {});
      // Create cache key for storing materials by project_id, project_type_id, pipe_id
      return responseData;
    } else {
      customNotification.error('Something went wrong.', 'Failed to fetch materials.');
      return null;
    }
  } catch (error) {
    customNotification.destroy();
    customNotification.error('Failed to fetch materials', error.message || error.error);
    return null;
  }
}


/**
 * Get all welder qualification register rows for a project
 * @param {Object} payload - Optional payload with project_id
 * @returns {Promise<Object|Array>} - API shape (array, { items }, or grouped object)
 */
export const getAllWelderQualification = async (payload = {}) => {
  try {
    let url = '/welderQualificationRegistry/getAllWelderQualifications';
    
    // Add project_id query param if provided
    if (payload.project_id) {
      url += `?project_id=${payload.project_id}`;
    }

    const res = await axiosConfig.get(url);

    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return {};
    }
    
    if (res.status === 200) {
      const data = get(res, ['data', 'data'], {});
      // Return data as-is (could be object grouped by qualification type or array)
      return data || {};
    }
    
    return {};
  } catch (error) {
    const errorMessage = get(error, 'response.data.data', error.data || error.message || 'Unknown error');
    const errorStatusCode = get(error, 'response.status', error.statusCode);
    
    if (errorStatusCode === 410 || (typeof errorMessage === 'string' && errorMessage.includes('Unknown column'))) {
      console.error('Backend database schema error:', errorMessage);
      customNotification.error(
        'Database Error', 
        'There is a database schema mismatch. Please contact the backend team to fix the SQL query. Error: ' + errorMessage
      );
    } else {
      customNotification.error('Failed to fetch welder qualifications', errorMessage);
    }
    return {};
  }
};

/**
 * Create or update welder qualification rows in bulk
 * @param {Object} payload - { items: [ { project_id, weld_number, welder_name, ... } ] }
 * @returns {Promise<Object|null>} - Response body from API on success
 */
export const bulkCreateUpdateWelderQualification = async (payload) => {
  try {
    const url = '/welderQualificationRegistry/bulkCreateUpdateWelderQualifications';

    const res = await axiosConfig.post(url, payload);

    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return null;
    }
    
    if (res.status === 200) {
      const responseData = get(res, ['data', 'data'], {});
      customNotification.success(
        'Welder Qualification saved successfully',
        'The Welder Qualification has been saved successfully.'
      );
      return responseData;
    }
    
    return null;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to save Welder Qualification';
    customNotification.error('Failed to save Welder Qualification', errorMessage);
    return null;
  }
};

/**
 * Delete welder qualifications in bulk
 * @param {Object} payload - Object with ids array: { ids: [1, 2, 3, ...] }
 * @returns {Promise<Object>} - Response with deletion statistics
 */
export const bulkDeleteWelderQualification = async (payload) => {
  try {
    const url = '/welderQualificationRegistry/bulkDeleteWelderQualifications';

    const res = await axiosConfig.post(url, payload);

    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return null;
    }
    
    if (res.status === 200) {
      const responseData = get(res, ['data', 'data'], {});
      customNotification.success(
        'Welder Qualification deleted successfully',
        'The selected welder qualification rows have been deleted successfully.'
      );
      return responseData;
    }
    
    return null;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to delete welder qualification';
    customNotification.error('Failed to delete welder qualification', errorMessage);
    return null;
  }
};

/**
 * Delete welder qualification documents by document IDs
 * @param {Object} payload - Object with docIds array: { docIds: [1, 2, 3, ...] }
 * @returns {Promise<Object>} - Response with deletion statistics
 */
export const deleteWelderQualificationRowsByIds = async (payload) => {
  try {
    const url = '/welderQualificationRegistry/deleteWelderQualificationDocumentsByDocId';

    const res = await axiosConfig.post(url, payload);

    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return null;
    }
    
    if (res.status === 200) {
      const responseData = get(res, ['data', 'data'], {});
      customNotification.success(
        'Welder documents deleted successfully',
        'The selected welder documents have been deleted successfully.'
      );
      return responseData;
    }
    
    return null;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to delete welder documents';
    customNotification.error('Failed to delete welder documents', errorMessage);
    return null;
  }
};

/** Flatten API response grouped by misc_material_type_name (or "Uncategorized"). */
export const normalizeRegistryMiscellaneousList = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data !== 'object') return [];
  const out = [];
  Object.entries(data).forEach(([groupKey, rows]) => {
    const list = Array.isArray(rows) ? rows : [];
    const typeName =
      groupKey === 'Uncategorized' || groupKey === 'uncategorized' ? '' : groupKey;
    list.forEach((row) => {
      out.push({
        ...row,
        misc_material_type_name:
          row.misc_material_type_name ??
          row.miscMaterialTypeName ??
          (typeName || null),
      });
    });
  });
  return out;
};

export const getAllRegistryMiscellaneous = async (payload = {}) => {
  try {
    let url = '/registryMiscellaneous/getAllRegistryMiscellaneous';
    if (payload.project_id) {
      url += `?project_id=${payload.project_id}`;
    }
    const res = await axiosConfig.get(url);
    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return {};
    }
    if (res.status === 200) {
      return get(res, ['data', 'data'], {}) || {};
    }
    return {};
  } catch (error) {
    const errorMessage = get(error, 'response.data.message', error.data || error.message || 'Unknown error');
    customNotification.error('Failed to fetch miscellaneous registry', errorMessage);
    return {};
  }
};

export const getPreSignedUrlToUploadRegistryMiscellaneous = async (fileList, payload, version = 1) => {
  try {
    const uploadedFiles = [];
    if (fileList && fileList.length > 0) {
      const fileObjs = fileList.map((file) => ({
        fileName: file.name.replace(/\(\d+\)/g, '').trim(),
        contentType: file.type,
        projectId: payload.projectId,
        projectTypeId: payload.projectTypeId,
        type: payload.type || 'MISCELLANEOUS_REGISTRY',
        fileSize: file.size,
        version: version || 1,
      }));
      const response = await axiosConfig.post(
        '/registryMiscellaneous/getPresignedUrlToUploadRegistryMiscellaneousDoc',
        fileObjs
      );
      if (response.status === 200) {
        const presignedData = response.data.data;
        for (const data of presignedData) {
          const file = fileList.find((f) => f.name.replace(/\(\d+\)/g, '').trim() === data.fileName);
          if (!file || !data.presignedUrl) continue;
          await new Promise((resolve) => setTimeout(resolve, 100));
          const uploadResponse = await axios.put(data.presignedUrl, file, {
            headers: { 'Content-Type': file.type || 'application/octet-stream' },
          });
          if (uploadResponse.status === 200) {
            uploadedFiles.push({ s3Key: data.s3Key, fileName: data.fileName });
          }
        }
      }
    }
    return uploadedFiles;
  } catch (error) {
    return [];
  }
};

export const bulkCreateUpdateRegistryMiscellaneous = async (payload) => {
  try {
    const url = '/registryMiscellaneous/bulkCreateUpdateRegistryMiscellaneous';
    const res = await axiosConfig.post(url, payload);
    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return null;
    }
    if (res.status === 200) {
      const responseData = get(res, ['data', 'data'], {});
      customNotification.success(
        'Miscellaneous registry saved',
        'The miscellaneous registry item has been saved successfully.'
      );
      return responseData;
    }
    return null;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to save';
    customNotification.error('Failed to save miscellaneous registry', errorMessage);
    return null;
  }
};

export const bulkDeleteRegistryMiscellaneous = async (payload) => {
  try {
    const url = '/registryMiscellaneous/bulkDeleteRegistryMiscellaneous';
    const res = await axiosConfig.post(url, payload);
    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return null;
    }
    if (res.status === 200) {
      const responseData = get(res, ['data', 'data'], {});
      customNotification.success(
        'Items deleted',
        'The selected miscellaneous registry items have been deleted.'
      );
      return responseData;
    }
    return null;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to delete';
    customNotification.error('Failed to delete miscellaneous registry items', errorMessage);
    return null;
  }
};

export const deleteRegistryMiscellaneousDocumentsByDocId = async (payload) => {
  try {
    const rawIds = payload?.docIds ?? payload?.doc_ids ?? [];
    const docIds = (Array.isArray(rawIds) ? rawIds : [])
      .map((id) => {
        if (typeof id === 'number' && Number.isInteger(id) && id >= 1) return id;
        const n = parseInt(String(id).trim(), 10);
        return Number.isInteger(n) && n >= 1 ? n : null;
      })
      .filter((id) => id != null);
    if (docIds.length === 0) {
      customNotification.error('Invalid request', 'No valid document ids to delete.');
      return null;
    }
    const url = '/registryMiscellaneous/deleteRegistryMiscellaneousDocumentsByDocId';
    const res = await axiosConfig.post(url, { docIds });
    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return null;
    }
    if (res.status === 200) {
      const responseData = get(res, ['data', 'data'], {});
      customNotification.success('Documents deleted', 'The selected documents have been deleted.');
      return responseData;
    }
    return null;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to delete documents';
    customNotification.error('Failed to delete documents', errorMessage);
    return null;
  }
};